import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, MenuItem, IconButton, Typography,
} from '@mui/material';
import { Delete, Undo, Edit } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { escapeHtml } from '../utils/html';

export interface DrawnFeature {
  id: string;
  name: string;
  code: string;
  agency_id: string; // '' = unassigned
  ring: [number, number][]; // [lat, lng] outer ring (unclosed)
}

interface AgencyOpt { id: number; name: string; abbreviation: string; }

interface Props {
  center: { lat: number; lng: number };
  zoom: number;
  agencies: AgencyOpt[];
  layerKind: 'agency' | 'beat';
  features: DrawnFeature[];
  onChange: (features: DrawnFeature[]) => void;
}

let idCounter = 0;
const nextId = () => `d${Date.now()}_${idCounter++}`;

export default function ZoneDrawMap({ center, zoom, agencies, layerKind, features, onChange }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef<L.Map | null>(null);
  const savedGroup = useRef<L.LayerGroup | null>(null);
  const draftGroup = useRef<L.LayerGroup | null>(null);

  const [drawing, setDrawing] = useState(false);
  const [points, setPoints] = useState<[number, number][]>([]);
  const drawingRef = useRef(drawing);
  drawingRef.current = drawing;

  // init map once
  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { maxZoom: 22 }).setView([center.lat, center.lng], zoom || 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OSM', maxZoom: 22 }).addTo(map);
    savedGroup.current = L.layerGroup().addTo(map);
    draftGroup.current = L.layerGroup().addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (!drawingRef.current) return;
      setPoints((prev) => [...prev, [e.latlng.lat, e.latlng.lng]]);
    });
    mapObj.current = map;
    // Ensure correct sizing after the dialog finishes opening.
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapObj.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // redraw the in-progress polygon
  useEffect(() => {
    const g = draftGroup.current;
    if (!g) return;
    g.clearLayers();
    if (points.length > 0) {
      points.forEach((p) => L.circleMarker(p, { radius: 4, color: '#d32f2f', fillColor: '#d32f2f', fillOpacity: 1 }).addTo(g));
      if (points.length >= 2) {
        L.polyline([...points, ...(points.length >= 3 ? [points[0]] : [])], { color: '#d32f2f', dashArray: '4 4' }).addTo(g);
      }
    }
  }, [points]);

  // render saved polygons
  useEffect(() => {
    const g = savedGroup.current;
    if (!g) return;
    g.clearLayers();
    features.forEach((f) => {
      if (f.ring.length < 3) return;
      const poly = L.polygon(f.ring, { color: '#3388ff', weight: 2, fillOpacity: 0.2 }).addTo(g);
      poly.bindTooltip(escapeHtml(f.name || '(unnamed)'), { permanent: false });
    });
  }, [features]);

  const finishPolygon = () => {
    if (points.length < 3) return;
    const feat: DrawnFeature = {
      id: nextId(),
      name: `Zone ${features.length + 1}`,
      code: '',
      agency_id: '',
      ring: points,
    };
    onChange([...features, feat]);
    setPoints([]);
    setDrawing(false);
  };

  const cancelDraw = () => { setPoints([]); setDrawing(false); };
  const undoPoint = () => setPoints((prev) => prev.slice(0, -1));

  const updateFeature = (id: string, patch: Partial<DrawnFeature>) =>
    onChange(features.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const removeFeature = (id: string) => onChange(features.filter((f) => f.id !== id));

  const zoomTo = (f: DrawnFeature) => {
    if (mapObj.current && f.ring.length >= 3) {
      mapObj.current.fitBounds(L.polygon(f.ring).getBounds(), { padding: [30, 30] });
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {!drawing ? (
          <Button size="small" variant="contained" onClick={() => { setPoints([]); setDrawing(true); }}>
            Draw new zone
          </Button>
        ) : (
          <>
            <Button size="small" variant="contained" color="success" disabled={points.length < 3} onClick={finishPolygon}>
              Finish polygon ({points.length})
            </Button>
            <Button size="small" startIcon={<Undo />} disabled={!points.length} onClick={undoPoint}>Undo point</Button>
            <Button size="small" color="inherit" onClick={cancelDraw}>Cancel</Button>
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          {drawing ? 'Click the map to add corners; at least 3 to finish.' : 'Draw one polygon per zone.'}
        </Typography>
      </Stack>

      <Box ref={mapRef} sx={{ height: 340, width: '100%', borderRadius: 1, border: '1px solid #0002', mb: 1 }} />

      {features.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>{layerKind === 'beat' ? 'Parent Agency' : 'Agency'}</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((f) => (
              <TableRow key={f.id}>
                <TableCell>
                  <TextField size="small" variant="standard" value={f.name} onChange={(e) => updateFeature(f.id, { name: e.target.value })} />
                </TableCell>
                <TableCell>
                  <TextField size="small" variant="standard" value={f.code} sx={{ width: 80 }} onChange={(e) => updateFeature(f.id, { code: e.target.value })} />
                </TableCell>
                <TableCell>
                  <TextField select size="small" variant="standard" value={f.agency_id} sx={{ minWidth: 160 }} onChange={(e) => updateFeature(f.id, { agency_id: e.target.value })}>
                    <MenuItem value="">(none)</MenuItem>
                    {agencies.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name} ({a.abbreviation})</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="Zoom to zone" onClick={() => zoomTo(f)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" title="Delete zone" onClick={() => removeFeature(f.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
